import type { BooksRole } from './stores/auth.js';

// apps/accounting/web/src/api.ts
// Access token kept in memory only (never localStorage - it's a 15-minute
// JWT and localStorage is readable by any script on the page, which turns
// one XSS bug into a stolen-session bug). Refreshed via identity's httpOnly
// refresh-token cookie, scoped to the identity domain - this app never
// reads that cookie directly, it just calls identity's /auth/refresh with
// credentials included and the browser attaches the cookie automatically.
// Mirrors apps/identity/admin/src/api.ts and apps/identity/login's pattern.

// VITE_API_URL is already configured as a Cloudflare Pages env var for this
// project (set when the Worker was first deployed) - reusing that name
// rather than introducing a new, unconfigured one. Identity's URL is also
// env-configurable via VITE_IDENTITY_API_URL so it can be changed in one
// place (dashboard env var or local .env) without a grep-and-replace.
const _BOOKS_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8790';
// Guard against misconfigured Pages dashboard env vars where VITE_API_URL
// may accidentally be set to the identity/login URL instead of the Books API.
export const BOOKS_API_URL = _BOOKS_API_URL.includes('identity-login') || _BOOKS_API_URL.includes('skarion-identity-login')
  ? 'https://skarion-books-platform.skarion-talentos.workers.dev'
  : _BOOKS_API_URL;
export const IDENTITY_API_URL =
  import.meta.env.VITE_IDENTITY_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:8787' : 'https://skarion-identity.skarion-talentos.workers.dev');
// The login page is a separate Pages site (not the Worker API). Separate env var so
// the redirect goes to the right place while API calls still hit the worker.
export const IDENTITY_LOGIN_URL =
  import.meta.env.VITE_IDENTITY_LOGIN_URL ||
  (import.meta.env.DEV ? 'http://localhost:5181' : 'https://skarion-identity-login-4hu.pages.dev');


let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
  }
}

function safeStorageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    console.warn('localStorage is disabled or blocked:', e);
  }
}

function safeStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.warn('localStorage is disabled or blocked:', e);
    return null;
  }
}

function safeStorageRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn('localStorage is disabled or blocked:', e);
  }
}

function extractHashTokens(): { accessToken: string; refreshToken: string } | null {
  console.log('[Auth] extractHashTokens: checking hash', window.location.hash ? '(present)' : '(empty)');
  try {
    const hash = window.location.hash;
    if (!hash || !hash.includes('access_token=')) {
      console.log('[Auth] extractHashTokens: no access_token in hash');
      return null;
    }
    const params = new URLSearchParams(hash.slice(1));
    const access = params.get('access_token');
    const refresh = params.get('refresh_token');
    console.log('[Auth] extractHashTokens: extracted access_token:', !!access, 'refresh_token:', !!refresh);
    if (access && refresh) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      return { accessToken: access, refreshToken: refresh };
    }
  } catch (err) {
    console.error('[Auth] Failed to extract tokens from hash:', err);
  }
  return null;
}

let refreshPromise: Promise<string | null> | null = null;
let bootstrapPromise: Promise<User | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) {
    console.log('[Auth] refreshAccessToken: reusing active refreshPromise');
    return refreshPromise;
  }

  console.log('[Auth] refreshAccessToken: starting token refresh...');
  refreshPromise = (async () => {
    try {
      const localRefreshToken = safeStorageGet('refresh_token');
      console.log('[Auth] refreshAccessToken: local refresh_token exists:', !!localRefreshToken);
      const response = await fetch(`${IDENTITY_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: localRefreshToken }),
        credentials: 'include',
      });
      console.log('[Auth] refreshAccessToken: /auth/refresh response status:', response.status, 'ok:', response.ok);
      if (!response.ok) {
        accessToken = null;
        safeStorageRemove('refresh_token');
        return null;
      }
      const data = (await response.json()) as { access_token: string; refresh_token?: string };
      accessToken = data.access_token;
      if (data.refresh_token) {
        console.log('[Auth] refreshAccessToken: saving rotated refresh_token');
        safeStorageSet('refresh_token', data.refresh_token);
      }
      console.log('[Auth] refreshAccessToken: refresh successful');
      return accessToken;
    } catch (err) {
      console.error('[Auth] refreshAccessToken: refresh failed with error:', err);
      accessToken = null;
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function bootstrapAuth(): Promise<User | null> {
  if (bootstrapPromise) {
    console.log('[Auth] bootstrapAuth: reusing active bootstrapPromise');
    return bootstrapPromise;
  }

  console.log('[Auth] bootstrapAuth: starting auth bootstrap...');
  bootstrapPromise = (async () => {
    try {
      const hashTokens = extractHashTokens();
      if (hashTokens) {
        console.log('[Auth] bootstrapAuth: hash tokens found, validating...');
        accessToken = hashTokens.accessToken;
        safeStorageSet('refresh_token', hashTokens.refreshToken);
        try {
          console.log('[Auth] bootstrapAuth: fetching /me to validate access token...');
          const response = await fetch(`${IDENTITY_API_URL}/me`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          console.log('[Auth] bootstrapAuth: /me response status:', response.status, 'ok:', response.ok);
          if (response.ok) {
            const data = await response.json();
            console.log('[Auth] bootstrapAuth: /me validation successful, user:', data.email);
            return {
              id: data.id,
              email: data.email,
              name: data.displayName,
              role: (data.apps?.books ?? '') as BooksRole,
              isSuperadmin: data.isSuperadmin,
            };
          } else {
            console.warn('[Auth] bootstrapAuth: /me returned non-ok status:', response.status);
          }
        } catch (meErr) {
          console.error('[Auth] bootstrapAuth: /me fetch failed with error:', meErr);
        }
      }

      console.log('[Auth] bootstrapAuth: no valid hash session, trying refresh token fallback...');
      const localRefreshToken = safeStorageGet('refresh_token');
      console.log('[Auth] bootstrapAuth: local refresh token exists:', !!localRefreshToken);
      const response = await fetch(`${IDENTITY_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: localRefreshToken }),
        credentials: 'include',
      });
      console.log('[Auth] bootstrapAuth: fallback /auth/refresh response status:', response.status, 'ok:', response.ok);
      if (!response.ok) {
        console.warn('[Auth] bootstrapAuth: fallback refresh failed, clearing token');
        safeStorageRemove('refresh_token');
        return null;
      }
      const data = (await response.json()) as AuthResponse;
      accessToken = data.access_token;
      if (data.refresh_token) {
        console.log('[Auth] bootstrapAuth: saving fallback rotated refresh_token');
        safeStorageSet('refresh_token', data.refresh_token);
      }
      console.log('[Auth] bootstrapAuth: fallback refresh successful, user:', data.user.email);
      return {
        id: data.user.id,
        email: data.user.email,
        name: data.user.displayName,
        role: (data.user.apps?.books ?? '') as BooksRole,
        isSuperadmin: data.user.isSuperadmin,
      };
    } catch (err) {
      console.error('[Auth] bootstrapAuth: bootstrap process failed with error:', err);
      return null;
    } finally {
      bootstrapPromise = null;
    }
  })();

  return bootstrapPromise;
}

export function redirectToLogin(): void {
  const returnTo = encodeURIComponent(window.location.href);
  console.log('[Auth] redirectToLogin: redirecting to login with returnTo:', window.location.href);
  window.location.href = `${IDENTITY_LOGIN_URL}/?return_to=${returnTo}`;
}

export async function booksFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  console.log(`[API] booksFetch: request to ${path}`);
  if (!accessToken) {
    console.log('[API] booksFetch: no access token in memory');
    if (!bootstrapPromise) {
      console.log('[API] booksFetch: bootstrapPromise is null, triggering bootstrapAuth...');
      bootstrapAuth();
    }
    console.log('[API] booksFetch: awaiting bootstrapPromise...');
    const user = await bootstrapPromise;
    if (!user) {
      console.warn('[API] booksFetch: bootstrap resolved to null, redirecting...');
      redirectToLogin();
      throw new ApiError('No session.', 401);
    }
  }

  const url = `${BOOKS_API_URL}${path}`;
  const headers = {
    ...(init.body instanceof FormData
      ? {}
      : { 'Content-Type': 'application/json' }),
    Authorization: `Bearer ${accessToken}`,
    ...init.headers,
  };

  console.log(`[API] booksFetch: fetching ${url}...`);
  let response = await fetch(url, {
    ...init,
    headers,
  });
  console.log(`[API] booksFetch: ${path} response status:`, response.status);

  if (response.status === 401) {
    console.log('[API] booksFetch: token unauthorized (401), refreshing...');
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      console.warn('[API] booksFetch: refresh failed on 401, redirecting...');
      redirectToLogin();
      throw new ApiError('Session expired.', 401);
    }
    console.log(`[API] booksFetch: retrying ${path} with new token...`);
    response = await fetch(`${BOOKS_API_URL}${path}`, {
      ...init,
      headers: {
        ...(init.body instanceof FormData
          ? {}
          : { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${accessToken}`,
        ...init.headers,
      },
    });
    console.log(`[API] booksFetch: retried ${path} response status:`, response.status);
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    console.error(`[API] booksFetch: request to ${path} failed:`, body.error);
    throw new ApiError(body.error ?? 'Request failed', response.status);
  }
  return response.json() as Promise<T>;
}

export interface User {
  id: string;
  email: string;
  name?: string;
  role: BooksRole;
  isSuperadmin: boolean;
}

export interface AppMembership {
  crm?: string;
  books?: string;
  hr?: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token?: string;
  user: {
    id: string;
    email: string;
    displayName?: string;
    isSuperadmin: boolean;
    apps: AppMembership;
  };
}

export interface Account {
  id: string;
  code: string;
  name: string;
  type: string;
  balance: string;
  createdAt: string;
  updatedAt: string;
}

export interface Transaction {
  id: string;
  description: string;
  amount: string;
  date: string;
  accountId: string;
  createdAt: string;
  updatedAt: string;
}

export interface Invoice {
  id: string;
  number: string;
  customerName: string;
  amount: string;
  status: string;
  dueDate: string;
  createdAt: string;
  updatedAt: string;
}
